// Endereços do site público por língua — fonte única para o site (rotas, pré-renderização) e para o backend
// (links nos emails, redirecionamentos de endereços antigos). Módulo puro: `@ecliptica/i18n/site-paths`.
//
// Esquema: a língua por omissão (DEFAULT_LANG) vive sem prefixo ("/", "/account/sign-in");
// as outras línguas ATIVAS levam "/<língua>" à frente ("/pt/conta/entrar").
// Endereços de línguas inativas ou com prefixo desnecessário redirecionam (ver `siteRedirect`).
import { ACTIVE_LANGS, DEFAULT_LANG, isActiveLang, isLang, LANGS, type Lang } from "./langs";

export type AccountPage = "account" | "signIn" | "register" | "forgot" | "reset" | "verified";
export type HelpPage = "help" | "helpNew" | "helpList";
export type LegalPage = "terms" | "privacy" | "cookies";
export type SitePage = "home" | "world" | "cards" | AccountPage | HelpPage | LegalPage;

/** Caminho de cada página em cada língua, SEM o prefixo da língua. */
const SLUGS: Record<Lang, Record<SitePage, string>> = {
  pt: {
    home: "/",
    world: "/mundo",
    cards: "/cartas",
    account: "/conta",
    signIn: "/conta/entrar",
    register: "/conta/registar",
    forgot: "/conta/recuperar",
    reset: "/conta/repor",
    verified: "/conta/confirmado",
    help: "/ajuda",
    helpNew: "/ajuda/novo",
    helpList: "/ajuda/pedidos",
    terms: "/termos",
    privacy: "/privacidade",
    cookies: "/cookies",
  },
  en: {
    home: "/",
    world: "/world",
    cards: "/cards",
    account: "/account",
    signIn: "/account/sign-in",
    register: "/account/register",
    forgot: "/account/forgot",
    reset: "/account/reset",
    verified: "/account/verified",
    help: "/help",
    helpNew: "/help/new",
    helpList: "/help/requests",
    terms: "/terms",
    privacy: "/privacy",
    cookies: "/cookies",
  },
};

const PAGE_KEYS = Object.keys(SLUGS.en) as SitePage[];

/** Prefixo da língua no endereço: "" para a por omissão, "/<língua>" para as outras. */
export function langPrefix(lang: Lang): string {
  return lang === DEFAULT_LANG ? "" : `/${lang}`;
}

/** Página inicial de uma língua ("/" ou "/pt"). */
export function pathFor(lang: Lang): string {
  return langPrefix(lang) || "/";
}

export function sitePath(lang: Lang, page: SitePage): string {
  const slug = SLUGS[lang][page];
  return slug === "/" ? pathFor(lang) : langPrefix(lang) + slug;
}

export const accountPath = (lang: Lang, page: AccountPage) => sitePath(lang, page);
export const helpPath = (lang: Lang, page: HelpPage) => sitePath(lang, page);
export const legalPath = (lang: Lang, page: LegalPage) => sitePath(lang, page);

export const LEGAL_PAGES: readonly LegalPage[] = ["terms", "privacy", "cookies"];

/** O endereço é de uma página legal (em qualquer língua, com ou sem ".html")? Serve a parte "legal" do site (404 se desligada). */
export function isLegalPath(pathname: string): boolean {
  const path = clean(pathname.replace(/\.html$/, ""));
  return LANGS.some((l) => LEGAL_PAGES.some((p) => path === sitePath(l, p) || path === SLUGS[l][p]));
}

/** Página de um pedido: /help/requests/T-2026-0001 (o código de acesso, se houver, vai em ?token=). */
export function ticketPath(lang: Lang, number: string, token?: string | null): string {
  return `${sitePath(lang, "helpList")}/${number}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
}

/** Língua de um endereço do site: a língua ativa do prefixo, ou a por omissão. */
export function langFromPath(pathname: string): Lang {
  const first = pathname.split("/")[1];
  return isActiveLang(first) && first !== DEFAULT_LANG ? first : DEFAULT_LANG;
}

/**
 * O Mundo: /world, /world/sun, /world/sun/auren, /world/sun/auren/order-of-dawn, /world/characters/teo.
 * Os segmentos a seguir ao da página são identificadores do conteúdo (iguais em todas as línguas); a lista
 * completa vive em `@ecliptica/game-ui/world`. Aqui só se monta o caminho.
 */
export function worldPath(lang: Lang, segments: readonly string[] = []): string {
  return segments.reduce((p, s) => `${p}/${s}`, sitePath(lang, "world"));
}

/** Página (ou pedido de ajuda, ou nó do Mundo) correspondente a um caminho sem prefixo, nos endereços de `lang`. */
function matchPage(lang: Lang, rest: string): { page: SitePage } | { ticket: string } | { world: string } | null {
  const page = PAGE_KEYS.find((p) => SLUGS[lang][p] === rest);
  if (page) return { page };
  const world = `${SLUGS[lang].world}/`;
  if (rest.startsWith(world) && rest.length > world.length) return { world: rest.slice(SLUGS[lang].world.length) };
  const list = `${SLUGS[lang].helpList}/`;
  if (rest.startsWith(list) && rest.length > list.length && !rest.slice(list.length).includes("/")) return { ticket: rest.slice(list.length) };
  return null;
}

/** Endereço equivalente em `to` de uma correspondência de `matchPage`. */
function mapped(to: Lang, m: NonNullable<ReturnType<typeof matchPage>>): string {
  if ("page" in m) return sitePath(to, m.page);
  if ("world" in m) return sitePath(to, "world") + m.world;
  return ticketPath(to, m.ticket);
}

const clean = (p: string) => p.replace(/\/+$/, "") || "/";

/** Página legal de um endereço do site (null se não for). */
export function legalPageOf(pathname: string): LegalPage | null {
  const from = langFromPath(pathname);
  const rest = clean(clean(pathname).slice(langPrefix(from).length) || "/");
  return LEGAL_PAGES.find((p) => SLUGS[from][p] === rest) ?? null;
}

/** O endereço é o da página das cartas (a galeria)? */
export function isCardsPage(pathname: string): boolean {
  const from = langFromPath(pathname);
  const rest = clean(clean(pathname).slice(langPrefix(from).length) || "/");
  return SLUGS[from].cards === rest;
}

/** Segmentos do Mundo de um endereço ([] = a raiz /world); null se o endereço não é do Mundo. */
export function worldSegments(pathname: string): string[] | null {
  const from = langFromPath(pathname);
  const rest = clean(clean(pathname).slice(langPrefix(from).length) || "/");
  const m = matchPage(from, rest);
  if (!m) return null;
  if ("world" in m) return m.world.split("/").filter(Boolean);
  return "page" in m && m.page === "world" ? [] : null;
}

/** A mesma página noutra língua (para o botão de língua): "/account/sign-in" → "/pt/conta/entrar". */
export function translatePath(pathname: string, to: Lang): string {
  const from = langFromPath(pathname);
  const rest = clean(clean(pathname).slice(langPrefix(from).length) || "/");
  const m = matchPage(from, rest);
  if (!m) return pathFor(to);
  return mapped(to, m);
}

/**
 * Para onde redirecionar um endereço que não é canónico (null = servir normalmente). Usado pelo backend (static.ts):
 * - prefixo da língua por omissão ou de uma língua inativa: "/en/account" → "/account", "/pt/conta/entrar" → "/account/sign-in";
 * - endereço sem prefixo noutra língua que não a por omissão (links antigos, ex. emails PT): "/conta/repor" → "/account/reset"
 *   (ou "/pt/conta/repor" se o PT estiver ativo).
 * Línguas inativas vão sempre para a página equivalente na língua por omissão.
 */
export function siteRedirect(pathname: string): string | null {
  const path = clean(pathname);
  const first = path.split("/")[1];
  const target = (from: Lang, rest: string): string | null => {
    const m = matchPage(from, rest);
    if (!m) return null;
    return mapped(isActiveLang(from) ? from : DEFAULT_LANG, m);
  };
  let out: string | null;
  if (isLang(first)) {
    // Prefixo de uma língua ativa que não a por omissão: já é canónico.
    if (isActiveLang(first) && first !== DEFAULT_LANG) return null;
    const rest = path.slice(first.length + 1) || "/";
    out = target(first, rest) ?? rest;
  } else {
    if (matchPage(DEFAULT_LANG, path)) return null;
    const others = LANGS.filter((l) => l !== DEFAULT_LANG);
    out = others.map((l) => target(l, path)).find((x) => x !== null) ?? null;
  }
  return out && out !== path ? out : null;
}

/** Todas as páginas que o build pré-renderiza: as das línguas ATIVAS (as de um pedido concreto não: são dinâmicas). */
export const PRERENDER_ROUTES: string[] = ACTIVE_LANGS.flatMap((l) => PAGE_KEYS.map((p) => sitePath(l, p)));
